import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CURRENCY_CODE } from '../../config/constants';

export const DurationSelector = ({ state, dispatch, setCharges, permission }) => {
  const [isCustomDuration, setIsCustomDuration] = useState(state.custom_duration);
  const [prevDurationCost, setPrevDurationCost] = useState(0);

  const baseRate = permission.PAW_WALK_DURATION_RATE;
  const FREE_PAW_WALK = permission.FREE_PAW_WALK;

  const calculateDurationPrice = (minutes) => {
    const mins = parseInt(minutes);
    const freeMinutesTotal = FREE_PAW_WALK * 30;

    if (!isNaN(mins) && mins > 0) {
      const fullBlocksSelected = Math.floor(mins / 30) * 30;
      const freeMinutesApplied = Math.min(fullBlocksSelected, freeMinutesTotal);
      const billableMins = Math.max(mins - freeMinutesApplied, 0);

      const newDurationCost = (billableMins * baseRate).toFixed(2);
      setPrevDurationCost(newDurationCost);

      setCharges((prevData) => ({
        ...prevData,
        duration: parseFloat(newDurationCost),
      }));
    } else {
      setPrevDurationCost(0);
      setCharges((prevData) => ({
        ...prevData,
        duration: 0,
      }));
    }
  };

  useEffect(() => {
    if (state.duration_walk) {
      const minutes = state.duration_walk.replace(' minutes', '');
      calculateDurationPrice(minutes);
    }
  }, [state.duration_walk]);

  const predefinedDurations = [
    { value: "30 minutes", label: "30 minutes" },
    { value: "60 minutes", label: "1 hour" },
    { value: "90 minutes", label: "1 hour 30 minutes" },
    { value: "120 minutes", label: "2 hours" },
  ];

  return (
    <div className="mb-4 sm:mb-6 px-2 sm:px-4">
      <h2 className="text-xl sm:text-2xl font-semibold text-purple-700 text-center mb-3 sm:mb-4">
        How long should your pet enjoy their walk? 🐕
      </h2>
      <p className="text-sm sm:text-base text-center text-gray-600 mb-4 sm:mb-6 px-2">
        Pick the perfect time for your pet's adventure! We recommend at least 30 minutes for a fulfilling walk.
      </p>

      <div className="space-y-4 sm:space-y-6">
        <div>
          <label className="block text-base sm:text-lg font-medium text-gray-800 mb-2">
            Choose Duration
          </label>
          <select
            name="duration_walk"
            value={isCustomDuration ? 'custom' : state.duration_walk}
            onChange={(e) => {
              const value = e.target.value;
              if (value === 'custom') {
                setIsCustomDuration(true);
                dispatch({
                  type: "LOAD_ITEM",
                  payload: {
                    duration_walk: '',
                    custom_duration: true,
                    walkSlot: null,
                  }
                });
              } else {
                setIsCustomDuration(false);
                dispatch({
                  type: "LOAD_ITEM",
                  payload: {
                    duration_walk: value,
                    custom_duration: false,
                    walkSlot: null,
                  }
                });
              }
            }}
            className="block w-full p-2.5 sm:p-3 text-sm sm:text-base border border-purple-300 rounded-lg mb-2 sm:mb-4 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="0 minutes">Select Duration</option>
            {predefinedDurations.map((duration) => (
              <option key={duration.value} value={duration.value}>
                {duration.label}
              </option>
            ))}
            <option value="custom">Custom Duration</option>
          </select>
        </div>

        {isCustomDuration && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-3 sm:space-y-4"
          >
            <label className="block text-base sm:text-lg font-medium text-gray-800">
              Enter Custom Duration
            </label>
            <input
              type="number"
              placeholder="Enter duration in minutes"
              name="duration_walk"
              value={state.duration_walk.replace(" minutes", "")}
              onChange={(e) => {
                const value = e.target.value;

                if (value === "") {
                  dispatch({
                    type: "LOAD_ITEM",
                    payload: {
                      duration_walk: "",
                      walkSlot: null,
                      custom_duration: true,
                    },
                  });
                  return;
                }

                if (/^\d+$/.test(value)) {
                  const minutes = parseInt(value, 10);
                  dispatch({
                    type: "LOAD_ITEM",
                    payload: {
                      duration_walk: `${value} minutes`,
                      walkSlot: null,
                      custom_duration: true,
                    },
                  });
                }
              }}
              onBlur={(e) => {
                let minutes = parseInt(e.target.value, 10);

                if (isNaN(minutes)) return;

                if (minutes < 30) minutes = 30;
                if (minutes > 480) minutes = 480;

                dispatch({
                  type: "LOAD_ITEM",
                  payload: {
                    duration_walk: `${minutes} minutes`,
                    walkSlot: null,
                    custom_duration: true,
                  },
                });
              }}
              onKeyDown={(e) => {
                if ([".", "-", "e"].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              className="block w-full p-2.5 sm:p-3 text-sm sm:text-base border border-purple-300 rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              min="30"
              max="480"
              step="15"
            />

            <p className="text-xs sm:text-sm text-gray-500 italic">
              Enter duration in minutes (30–480). We recommend 30-minute intervals.
            </p>
          </motion.div>
        )}

        <div className="mt-4 sm:mt-6 rounded-xl border border-purple-200 bg-purple-50/50 p-4 sm:p-5 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm sm:text-base font-medium text-gray-700">
              Duration Cost
            </span>
            <span className="text-lg sm:text-xl font-bold text-purple-700">
              {prevDurationCost ? `${prevDurationCost} ${CURRENCY_CODE}` : `0 ${CURRENCY_CODE}`}
            </span>
          </div>

          {state.duration_walk && (() => {
            const selectedMinutes = parseInt(state.duration_walk.replace(" minutes", ""));
            const fullBlocksSelected = Math.floor(selectedMinutes / 30) * 30;
            const freeMinutesAvailable = FREE_PAW_WALK * 30;
            const freeMinutesApplied = Math.min(fullBlocksSelected, freeMinutesAvailable);
            const billableMinutes = Math.max(selectedMinutes - freeMinutesApplied, 0);

            return (
              <div className="mt-2 rounded-lg bg-white border border-purple-100 p-4 sm:p-5 text-sm text-gray-700 shadow-inner space-y-2">
                <div className="space-y-1">
                  <p>
                    🎁 You have <strong>{FREE_PAW_WALK}</strong> free walk {FREE_PAW_WALK === 1 ? "slot" : "slots"} — that’s up to{" "}
                    <span className="font-medium text-purple-600">{freeMinutesAvailable} minutes</span> on us.
                  </p>

                  <p>
                    ✅ This walk is <span className="font-medium text-purple-600">{selectedMinutes} minutes</span> long.{" "}
                    <span className="font-medium text-purple-600">{freeMinutesApplied} minutes</span> are covered for free.
                    {billableMinutes > 0 ? (
                      <>{" "}So, <span className="text-rose-600 font-semibold">{billableMinutes} minutes</span> will be billed.</>
                    ) : (
                      <span className="text-emerald-600 font-semibold"> You’re fully covered — no charge at all! 🎉</span>
                    )}
                  </p>
                </div>

                {/* {billableMinutes > 0 && (
                  <div className="text-[13px] text-gray-500 leading-snug bg-purple-50/70 border border-purple-100 rounded-md p-3">
                    <p className="font-medium mb-1 text-purple-700">ℹ️ Why am I being charged for just a few minutes?</p>
                    <p>
                      Our system works in <strong>30-minute walk blocks</strong>. Since your selected duration doesn’t fully
                      complete another 30-minute block, the extra{" "}
                      <strong>{billableMinutes} minutes</strong> falls outside your plan and is billed accordingly.
                    </p>
                  </div>
                )} */}

                {/* <p className="text-[13px] text-gray-500 italic">
                  🐾 We aim for total transparency — no hidden charges, just fair breakdowns. Thanks for letting us care for your furry friend. 🐶
                </p> */}
              </div>
            );
          })()}


        </div>
      </div>
    </div>
  );
};
